import styled from "@emotion/styled";
import { FC, useState } from "react";

import { Heading, Modal, RadiusToken, SpacingToken, Text } from "@livechat/design-system-react-components";

import logoContext from "assets/svg/logo-context-2025.svg";
import { Context2025PromoLinkButton } from "components/Context2025PromoLinkButton";
import { campaigns, useLimitedTimeCampaign } from "hooks/use-limited-time-campaigns";
import { withVar } from "themes/helpers";

import context2025Promo from "./assets/context-2025-modal-background.png";
import spinImage from "./assets/spin-symbol.svg";

const CUSTOM_TEXT_COLOR = "#f8ffed";
const CUSTOM_BACKGROUND_COLOR = "#0f1d18";
const LANDING_PAGE_URL =
  "http://www.text.com/context25?utm_source=app.helpdesk.com&utm_medium=popup&utm_campaign=context25&utm_content=button";

export const Context2025PromoModal: FC = () => {
  const { isCampaignActive: isContext2025PromoCampaignActive, setCampaignFlagValue: setAsSeen } =
    useLimitedTimeCampaign(campaigns.context2025PromoModal);
  const [isModalVisible, setIsModalVisible] = useState(isContext2025PromoCampaignActive);

  const setModalAsSeen = (): void => {
    setIsModalVisible(false);
    setAsSeen(new Date().toISOString());
  };

  if (!isModalVisible) return null;

  return (
    <StyledModal
      onClose={setModalAsSeen}
      fullSpaceContent
      labelHeading={<div aria-hidden="true" />} // close button outside the modal
      closeOnOverlayPress={false}
      closeOnEscPress={false}
    >
      <ContentWrapper>
        <Header>
          <ContextLogo src={logoContext} alt="Context 2025" />
          <Subheader>
            <DateText>Sept 30, 2025</DateText>
            <img src={spinImage} alt="Spin image" />
            <DateText>11:00 a.m. EDT</DateText>
          </Subheader>
        </Header>
        <Content>
          <Title as="h2">The future of customer service starts here</Title>
          <Description>
            Text App launches at Context ’25. A smarter way to work — built to drive loyalty, accelerate growth, and
            scale personal support like never before.
          </Description>
          <StyledContext2025PromoLinkButton href={LANDING_PAGE_URL} onClick={setModalAsSeen} />
        </Content>
      </ContentWrapper>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  background-color: ${CUSTOM_BACKGROUND_COLOR};
  width: 600px;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  border-radius: ${withVar(RadiusToken.Radius3)} ${withVar(RadiusToken.Radius3)} 0 0;
  aspect-ratio: 900 / 506; // actual image dimensions to avoid image distortion
  background: url(${context2025Promo}) center / contain no-repeat;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${withVar(SpacingToken.Spacing5)};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${withVar(SpacingToken.Spacing4)};
  text-align: center;
  border-radius: 0 0 ${withVar(RadiusToken.Radius3)} ${withVar(RadiusToken.Radius3)};

  padding: ${withVar(SpacingToken.Spacing8)} ${withVar(SpacingToken.Spacing5)} ${withVar(SpacingToken.Spacing16)};
`;

const DateText = styled(Text)`
  font-family: Arial, sans-serif;
  font-size: 24px;
  font-weight: 400;
  line-height: 140%;
  color: ${CUSTOM_TEXT_COLOR};
`;

const Subheader = styled.div`
  display: flex;
  align-items: center;
  gap: ${withVar(SpacingToken.Spacing4)};
`;

const Title = styled(Heading)`
  font-family: Lora, sans-serif;
  font-size: 28px;
  font-weight: 600;
  line-height: 110%;
  max-width: 460px;
  color: ${CUSTOM_TEXT_COLOR};
`;

const Description = styled(Text)`
  font-family: Fustat, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 145%;
  max-width: 542px;
  color: ${CUSTOM_TEXT_COLOR};
`;

const ContextLogo = styled.img`
  height: 52px;
  margin-top: ${withVar(SpacingToken.Spacing18)};
`;

const StyledContext2025PromoLinkButton = styled(Context2025PromoLinkButton)`
  margin-top: ${withVar(SpacingToken.Spacing4)};
`;
